import { ReactElement, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { heapEvent } from '@equipmentshare/heaper'
import {
	useDistributionCentersQuery,
	useCreateStorePartMutation,
} from '../../../../../../graphql'
import { useShowError, useShowSuccess } from 'hooks/useToast'

type Props = {
	open: boolean,
	itemMasterId: string,
	variantProductIds: string[],
	trackedInventoryLocationIds: Set<string>,
	onClose: () => void,
	onAdded: () => void,
}

export default function AddToDistributionCenterDialog( {
	open,
	itemMasterId,
	variantProductIds,
	trackedInventoryLocationIds,
	onClose,
	onAdded,
}: Props ): ReactElement {
	const [ filter, setFilter ] = useState( '' )
	const [ selected, setSelected ] = useState<Set<string>>( new Set() )
	const [ submitting, setSubmitting ] = useState( false )

	const showSuccess = useShowSuccess()
	const showError = useShowError()

	const { data, loading } = useDistributionCentersQuery( {
		variables: { paging: { first: 100 } },
		skip: !open,
		fetchPolicy: 'cache-and-network',
	} )

	const [ createStorePart ] = useCreateStorePartMutation()

	const allDCs = useMemo( () => {
		return data?.inventoryLocations.edges.map( ( edge ) => edge.node ) ?? []
	}, [ data ] )

	const availableDCs = useMemo( () => {
		const normalized = filter.trim().toLowerCase()
		return allDCs
			.filter( ( dc ) => !trackedInventoryLocationIds.has( dc.id ) )
			.filter( ( dc ) => {
				if ( !normalized ) {
					return true
				}
				const haystack = `${dc.name} ${dc.branch?.name ?? ''}`.toLowerCase()
				return haystack.includes( normalized )
			} )
	}, [ allDCs, trackedInventoryLocationIds, filter ] )

	const toggle = ( id: string ) => {
		setSelected( ( prev ) => {
			const next = new Set( prev )
			if ( next.has( id ) ) {
				next.delete( id )
			} else {
				next.add( id )
			}
			return next
		} )
	}

	const close = () => {
		setFilter( '' )
		setSelected( new Set() )
		onClose()
	}

	const handleSubmit = async() => {
		const dcIds = Array.from( selected )
		if ( dcIds.length === 0 || variantProductIds.length === 0 ) {
			return
		}
		setSubmitting( true )

		// One createStorePart per (DC, variant). Bucket results back to DC so we
		// can report success at the DC granularity — that's what the user picked.
		const tasks: Array<{ dcId: string, productId: string, promise: Promise<boolean> }> = []
		for ( const dcId of dcIds ) {
			for ( const productId of variantProductIds ) {
				const promise = createStorePart( {
					variables: {
						input: { productId, inventoryLocationId: dcId },
					},
				} ).then( ( { data: mutationData } ) => Boolean( mutationData?.createStorePart?.success ) )
					.catch( () => false )
				tasks.push( { dcId, productId, promise } )
			}
		}

		const settled = await Promise.all( tasks.map( ( t ) => t.promise ) )
		const dcOutcome = new Map<string, { ok: number, fail: number }>()
		dcIds.forEach( ( id ) => dcOutcome.set( id, { ok: 0, fail: 0 } ) )
		tasks.forEach( ( task, i ) => {
			const bucket = dcOutcome.get( task.dcId )!
			if ( settled[ i ] ) {
				bucket.ok += 1
			} else {
				bucket.fail += 1
			}
		} )

		// A DC counts as "added" when *every* variant succeeded for it. A partial
		// DC is treated as failed so the user knows to retry — they can use the
		// per-variant Start tracking action in the table to fill in the gaps.
		const succeededDCs: string[] = []
		const failedDCs: string[] = []
		for ( const dcId of dcIds ) {
			const outcome = dcOutcome.get( dcId )!
			if ( outcome.ok === variantProductIds.length ) {
				succeededDCs.push( dcId )
			} else {
				failedDCs.push( dcId )
			}
		}

		heapEvent( 'Inventory - Item Master - Detail - Add To DC - Submit', {
			item_master_id: itemMasterId,
			dcs_succeeded: succeededDCs.length,
			dcs_failed: failedDCs.length,
		} )

		if ( succeededDCs.length > 0 ) {
			showSuccess(
				`addToDcSuccess${Date.now()}`,
				succeededDCs.length === 1
					? 'Added to 1 distribution center.'
					: `Added to ${succeededDCs.length} distribution centers.`
			)
		}
		if ( failedDCs.length > 0 ) {
			const names = failedDCs
				.map( ( id ) => allDCs.find( ( d ) => d.id === id )?.name ?? id )
				.join( ', ' )
			showError(
				`addToDcError${Date.now()}`,
				`Some variants failed to add at: ${names}. Use the per-variant Start tracking action to retry.`
			)
		}

		setSubmitting( false )
		onAdded()
		close()
	}

	const selectedCount = selected.size

	return (
		<Dialog open={open} onClose={close} fullWidth maxWidth="sm">
			<DialogTitle>Add to Distribution Center</DialogTitle>
			<DialogContent dividers>
				<Typography variant="body2" color="text.secondary" mb={2}>
					Adds every variant of this Item Master to the selected distribution {selectedCount === 1 ? 'center' : 'centers'}. Bin locations can be set in the table afterwards.
				</Typography>
				<TextField
					value={filter}
					onChange={( e ) => setFilter( e.target.value )}
					placeholder="Search distribution centers"
					size="small"
					fullWidth
					autoFocus
					sx={{ mb: 2 }}
				/>
				{loading && allDCs.length === 0 && (
					<Typography variant="body2" color="text.secondary">
						Loading…
					</Typography>
				)}
				{!loading && availableDCs.length === 0 && (
					<Typography variant="body2" color="text.secondary">
						{allDCs.length === 0
							? 'No distribution centers found.'
							: trackedInventoryLocationIds.size >= allDCs.length
								? 'This item is already tracked at every distribution center.'
								: 'No distribution centers match the filter.'}
					</Typography>
				)}
				{availableDCs.length > 0 && (
					<Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
						<List dense disablePadding>
							{availableDCs.map( ( dc ) => (
								<ListItemButton
									key={dc.id}
									onClick={() => toggle( dc.id )}
									disabled={submitting}
								>
									<Checkbox
										edge="start"
										checked={selected.has( dc.id )}
										tabIndex={-1}
										disableRipple
									/>
									<ListItemText
										primary={dc.name}
										secondary={dc.branch?.name ?? null}
									/>
								</ListItemButton>
							) )}
						</List>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={close} disabled={submitting}>Cancel</Button>
				<Button
					variant="contained"
					onClick={handleSubmit}
					disabled={selectedCount === 0 || submitting || variantProductIds.length === 0}
				>
					{submitting
						? 'Adding…'
						: selectedCount === 0
							? 'Add'
							: `Add (${selectedCount})`}
				</Button>
			</DialogActions>
		</Dialog>
	)
}
